import { GLBModel } from "@/scene/GLBModel";
import { GLBNodeModel } from "@/scene/GLBNodeModel";
import type {
  AmbientFishConfig,
  EnvironmentDecorItemConfig,
} from "@/scene/SceneProps";
import type { UnderwaterVisualSettingsOverride } from "@/scene/VisualSettings";

export const FISH_DORADE_MODEL = new GLBModel("/models/fish/fish_dorade.glb");
export const CARP_FISH_MODEL = new GLBModel("/models/fish/carp_fish.glb");
export const NEON_FISH_MODEL = new GLBModel("/models/fish/neon.glb");
export const FISH_1_MODEL = new GLBModel("/models/fish/fish_01.glb");
export const FISH_2_MODEL = new GLBModel("/models/fish/fish_02.glb");
export const BLUE_TANG_MODEL = new GLBModel("/models/fish/10 Blue Tang.glb");
export const CLOWNFISH_BLACK_MODEL = new GLBModel("/models/fish/12 Clownfish Black.glb");
export const COPPERBAND_MODEL = new GLBModel("/models/fish/14 Copperband.glb");
export const ROCK_FORMAT2_MODEL = new GLBModel("/models/decoration/rock_format2.glb");
export const ROCK_MODEL = new GLBModel("/models/decoration/rock.glb");
export const LOG_MODEL = new GLBModel("/models/decoration/log.glb");
export const LOWPOLY_STICKS_MODEL = new GLBModel("/models/decoration/lowpoly_sticks.glb");
export const DRIFTWOOD_001_MODEL = new GLBModel("/models/decoration/driftwood_001.glb");
export const CLARET_WIGGLE_SEAWEED_MODEL = new GLBModel(
  "/models/decoration/claret_wiggle_seaweed.glb"
);
export const WAGON_WHEEL_MODEL = new GLBModel("/models/decoration/wagon_wheel.glb");
export const LOWPOLY_STICK_NODE_NAMES = [
  "stick01_lod4",
  "stick02_lod4",
  "stick03_lod4",
  "stick04_lod4",
  "stick05_lod4",
  "stick06_lod4",
  "stick07_lod4",
  "stick08_lod4",
  "stick09_lod4",
  "stick10_lod4",
  "stick11_lod4",
  "stick12_lod4",
  "stick13_lod4",
  "stick14_lod4",
  "stick15_lod4",
  "stick16_lod4",
  "stick17_lod4",
  "stick18_lod4",
  "stick19_lod4",
  "stick20_lod4",
] as const;
export const LOWPOLY_STICK_MODELS = LOWPOLY_STICK_NODE_NAMES.map((nodeName) =>
  new GLBNodeModel(
    "/models/decoration/lowpoly_sticks.glb",
    nodeName,
    `lowpoly_sticks_${nodeName}`
  )
);
export const ASSORTED_ROCK_MODELS = Array.from({ length: 32 }, (_, index) => {
  const nodeName = `AR${String(index + 1).padStart(2, "0")}`;
  return new GLBNodeModel(
    "/models/decoration/AssortedRocks.glb",
    nodeName,
    `assorted_rocks_${nodeName.toLowerCase()}`
  );
});
export const CORAL_NODE_NAMES = [
  "coral01",
  "coral10",
  "coral11",
  "coral12",
  "coral13",
  "coral14",
  "coral15",
  "coral16",
  "coral17",
  "coral18",
  "coral19",
  "coral20",
  "coral21",
  "coral22",
  "coral23",
  "coral24",
  "coral25",
  "coral26",
  "coral27",
  "coral28",
  "Coral02_low",
  "Coral01_low",
  "Coral02_low1",
  "Coral01_low1",
] as const;
export const CORAL_MODELS = CORAL_NODE_NAMES.map((nodeName) =>
  new GLBNodeModel(
    "/models/decoration/Corals_updated.glb",
    nodeName,
    `corals_updated_${nodeName.toLowerCase()}`
  )
);

export const VISUAL_LAB_DECOR_MODELS = [
  CLARET_WIGGLE_SEAWEED_MODEL,
  WAGON_WHEEL_MODEL,
  LOWPOLY_STICKS_MODEL,
  ...LOWPOLY_STICK_MODELS,
  DRIFTWOOD_001_MODEL,
  ...ASSORTED_ROCK_MODELS,
  ...CORAL_MODELS,
];

export const ALL_SCENES_CAUSTICS_VISUAL_SETTINGS: UnderwaterVisualSettingsOverride = {
  causticsOpacity: 0.59,
  causticsScale: 0.73,
  causticsSpeed: 0.35,
  causticsIntensity: 0.75,
  causticsRadiusScale: 1.59,
  surfaceGlowIntensity: 0,
  waterSurfaceColor: "#0f625e",
  waterSurfaceOpacity: 0.78,
  waterSurfaceHeight: 6.4,
  waterSurfaceWaveStrength: 0.38,
  waterSurfaceWaveScale: 1.0,
  waterSurfaceWaveSpeed: 0.78,
};

export const DEFAULT_AMBIENT_FISH: AmbientFishConfig = {
  visible: true,
  count: 24,
  pollutionCounts: {
    pollutedCount: 6,
    cleanCount: 24,
  },
  models: [
    // {
    //   model: FISH_DORADE_MODEL,
    //   targetSize: 0.42,
    //   rotation: { x: 0, y: 0, z: 0 },
    // },
    {
      model: CARP_FISH_MODEL,
      targetSize: 0.42,
      rotation: { x: 0, y: Math.PI / 2, z: 0 },
    },
  ],
};

export const NEONS_AMBIENT_FISH: AmbientFishConfig = {
  visible: true,
  count: 56,
  pollutionShoals: {
    pollutedCount: 2,
    cleanCount: 8,
  },
  shoals: {
    count: 8,
    fishPerShoal: 7,
    fishCounts: [5, 7, 9, 7, 7, 7, 7, 7],
    spread: { x: 0.5, y: 0.28, z: 0.3 },
  },
  models: [
    {
      model: NEON_FISH_MODEL,
      targetSize: 0.24,
      rotation: { x: 0, y: 0, z: 0 },
    },
  ],
};

// fish_1 плавает стаями (как неоны)
export const FISH_1_AMBIENT_FISH: AmbientFishConfig = {
  visible: true,
  count: 48,
  pollutionShoals: {
    pollutedCount: 2,
    cleanCount: 7,
  },
  shoals: {
    count: 7,
    fishPerShoal: 7,
    fishCounts: [5, 7, 8, 7, 6, 7, 8],
    spread: { x: 0.55, y: 0.3, z: 0.32 },
  },
  models: [
    {
      model: FISH_1_MODEL,
      targetSize: 0.26,
      rotation: { x: 0, y: 0, z: 0 },
    },
  ],
};

// fish_2 плавает поодиночке (как carp)
export const FISH_2_AMBIENT_FISH: AmbientFishConfig = {
  visible: true,
  count: 24,
  pollutionCounts: {
    pollutedCount: 6,
    cleanCount: 24,
  },
  models: [
    {
      model: FISH_2_MODEL,
      targetSize: 0.42,
      rotation: { x: 0, y: 0, z: 0 },
    },
  ],
};

// fish_2 + голубой хирург (для сцены со скатом/хирургом)
export const FISH_2_AND_BLUETANG_AMBIENT_FISH: AmbientFishConfig = {
  ...FISH_2_AMBIENT_FISH,
  models: [
    ...FISH_2_AMBIENT_FISH.models,
    {
      model: BLUE_TANG_MODEL,
      targetSize: 0.38,
      rotation: { x: 0, y: -Math.PI / 2, z: 0 },
    },
  ],
};

// carp + клоун (для инии)
export const CARP_AND_CLOWNFISH_AMBIENT_FISH: AmbientFishConfig = {
  ...DEFAULT_AMBIENT_FISH,
  models: [
    ...DEFAULT_AMBIENT_FISH.models,
    {
      model: CLOWNFISH_BLACK_MODEL,
      targetSize: 0.25,
      rotation: { x: 0, y: -Math.PI / 2, z: 0 },
    },
  ],
};

// fish_2 + клоун (для кита)
export const FISH_2_AND_CLOWNFISH_AMBIENT_FISH: AmbientFishConfig = {
  ...FISH_2_AMBIENT_FISH,
  models: [
    ...FISH_2_AMBIENT_FISH.models,
    {
      model: CLOWNFISH_BLACK_MODEL,
      targetSize: 0.25,
      rotation: { x: 0, y: -Math.PI / 2, z: 0 },
    },
  ],
};

// copperband плавает поодиночке (для неонов и осьминога — отдельным слоем)
export const COPPERBAND_INDIVIDUAL_AMBIENT_FISH: AmbientFishConfig = {
  visible: true,
  count: 8,
  pollutionCounts: {
    pollutedCount: 2,
    cleanCount: 8,
  },
  models: [
    {
      model: COPPERBAND_MODEL,
      targetSize: 0.24,
      rotation: { x: 0, y: -Math.PI / 2, z: 0 },
    },
  ],
};

export const DEFAULT_ENVIRONMENT_DECOR: EnvironmentDecorItemConfig[] = [
  {
    model: ROCK_FORMAT2_MODEL,
    targetSize: 0.78,
    position: { x: -1.65, y: -2.28, z: -1.15 },
    rotation: { x: 0, y: 0.42, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: ROCK_MODEL,
    targetSize: 0.58,
    position: { x: 1.65, y: -2.28, z: -0.9 },
    rotation: { x: 0, y: -0.7, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: ROCK_FORMAT2_MODEL,
    targetSize: 0.52,
    position: { x: 0.88, y: -2.29, z: 1.18 },
    rotation: { x: 0, y: 2.35, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: ROCK_MODEL,
    targetSize: 0.44,
    position: { x: -1.05, y: -2.3, z: 1.02 },
    rotation: { x: 0, y: 1.65, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: LOG_MODEL,
    targetSize: 1.35,
    position: { x: -0.18, y: -2.28, z: 1.62 },
    rotation: { x: 0.04, y: -0.78, z: 0.06 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: LOG_MODEL,
    targetSize: 2,
    position: { x: 0.04, y: -2.26, z: -1 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: LOG_MODEL,
    targetSize: 2,
    position: { x: 0.18, y: -2.28, z: 3 },
    rotation: { x: 0, y: -0.524, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: LOG_MODEL,
    targetSize: 2,
    position: { x: 3, y: -2, z: 0.36 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
  {
    model: ROCK_FORMAT2_MODEL,
    targetSize: 1.07,
    position: { x: 2, y: -2.27, z: 2 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  },
];
